import { useCallback, useEffect } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useKnowledgeSourcesStore, useKnowledgeSourcesActions } from './index';

/**
 * Ensures the connectors store is populated. Calls the knowledge configuration
 * API if the store is empty, otherwise no-ops.
 * Gated behind rovo_chat_3p_app_discovery_m1_experiment (isEnabled) — when false this is a no-op.
 */
export const useEnsureConnectorsLoaded: (
	props: Omit<AssistanceServiceInjectedParams, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => void = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker, siteId },
	}: AssistanceServiceInjectedParams) => {
		const [{ hasFetchAttempted, isFetching }] = useKnowledgeSourcesStore();
		const { fetchIfEmpty } = useKnowledgeSourcesActions();

		const fetcher = useCallback(async () => {
			experienceTracker.start({
				id: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE,
				name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE,
			});
			try {
				const response = await assistanceService.getAgentKnowledgeConfiguration();
				experienceTracker.succeed({
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE,
				});
				return response;
			} catch (error) {
				experienceTracker.fail({
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE,
					error: error instanceof Error ? error : new Error(String(error)),
				});
				throw error;
			}
		}, [assistanceService, experienceTracker]);

		useEffect(() => {
			if (
				!hasFetchAttempted &&
				!isFetching &&
				FeatureGates.getExperimentValue(
					'rovo_chat_3p_app_discovery_m1_experiment',
					'isEnabled',
					false,
				)
			) {
				fetchIfEmpty(fetcher, siteId);
			}
		}, [hasFetchAttempted, isFetching, fetchIfEmpty, fetcher, siteId]);
	},
);
